import React, { useState } from 'react';
import { HelpCircle, X, Mail, Phone, BookOpen, MessageCircle } from 'lucide-react';

/**
 * Floating support widget — bottom-right "?" button. Opens a small panel with
 * documentation link, email, phone, and a "send us a message" mailto link.
 *
 * Norwegian-first copy. Tap-targets sized for mobile.
 */
export default function SupportWidget() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Floating launcher button — hidden when panel is open */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Hjelp og support"
          data-testid="support-widget-button"
          className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-accent-blue hover:bg-accent-blue-hover text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          title="Trenger du hjelp?"
        >
          <HelpCircle size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-80 max-w-[calc(100vw-2rem)] bg-navy-800 border border-navy-600 rounded-xl shadow-2xl overflow-hidden"
          data-testid="support-widget-panel"
        >
          <div className="bg-gradient-to-r from-accent-blue/30 to-purple-500/20 border-b border-navy-600 px-5 py-4 flex items-start justify-between">
            <div>
              <h3 className="font-heading text-base font-semibold text-white">Trenger du hjelp?</h3>
              <p className="text-xs text-slate-300 mt-0.5">Vi svarer normalt innen én arbeidsdag.</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Lukk"
              className="text-slate-400 hover:text-white transition-colors flex-shrink-0 -mt-1"
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-3 space-y-1">
            <a
              href="https://airesepsjonen.no/dokumentasjon"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-navy-700 transition-colors group"
            >
              <div className="w-9 h-9 rounded-full bg-accent-blue/15 flex items-center justify-center flex-shrink-0">
                <BookOpen size={15} className="text-accent-blue" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-white group-hover:text-accent-blue transition-colors">Dokumentasjon</div>
                <div className="text-xs text-slate-500 truncate">Komme i gang, FAQ, integrasjoner</div>
              </div>
            </a>

            <a
              href="mailto:support@airesepsjonen.no"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-navy-700 transition-colors group"
              data-testid="support-email-link"
            >
              <div className="w-9 h-9 rounded-full bg-accent-green/15 flex items-center justify-center flex-shrink-0">
                <Mail size={15} className="text-accent-green" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-white group-hover:text-accent-green transition-colors">support@airesepsjonen.no</div>
                <div className="text-xs text-slate-500 truncate">E-post oss direkte</div>
              </div>
            </a>

            <a
              href="tel:+4723509823"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-navy-700 transition-colors group"
              data-testid="support-phone-link"
            >
              <div className="w-9 h-9 rounded-full bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                <Phone size={15} className="text-purple-400" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-white group-hover:text-purple-400 transition-colors">+47 23 50 98 23</div>
                <div className="text-xs text-slate-500 truncate">Hverdager 09:00–17:00</div>
              </div>
            </a>
          </div>

          <div className="border-t border-navy-600 p-3">
            <a
              href="mailto:support@airesepsjonen.no?subject=St%C3%B8tte%20fra%20portalen&body=Hei%2C%0A%0A"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="support-send-message"
            >
              <MessageCircle size={14} />
              Send oss en melding
            </a>
          </div>
        </div>
      )}
    </>
  );
}
